"use client";

import React from "react";
import Link from "next/link";
import {
  MultiUserDisplay,
  readAssignedUserRef,
} from "@/features/tasks/components/components/MultiUserDisplay";
import { UnifiedTaskRowActions } from "@/features/tasks/components/components/UnifiedTaskRowActions";
import { UnifiedTaskRowContextMenu } from "@/features/tasks/components/components/UnifiedTaskRowContextMenu";
import type { TaskRow } from "./types";
import { formatStatusLabel, formatTaskDate } from "./utils";

interface TicketTasksAllTableProps {
  tasks: TaskRow[];
  isLoading: boolean;
  error: unknown;
  refetch?: () => void | Promise<void>;
}

/**
 * "All tasks" sub-tab content: handles loading / error / empty states and
 * renders the full task list table when data is present.
 */
export function TicketTasksAllTable({
  tasks,
  isLoading,
  error,
  refetch,
}: TicketTasksAllTableProps) {
  if (isLoading) {
    return <p className="text-sm text-gray-500 py-6">Loading tasks…</p>;
  }

  if (error) {
    return (
      <p className="text-sm text-red-600 py-6">
        Could not load tasks. If this is a new environment, ensure the{" "}
        <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 rounded">ticket_id</code> column exists on{" "}
        <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 rounded">contact_tasks</code>.
      </p>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 bg-card-50 p-6 text-center text-sm text-gray-600 dark:text-gray-400">
        No tasks linked to this ticket yet. Create one to track work here.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700 bg-card-50 text-left text-gray-600 dark:text-gray-400">
            <th className="px-3 py-2 font-medium">Title</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Priority</th>
            <th className="px-3 py-2 font-medium">Due</th>
            <th className="px-3 py-2 font-medium">Assignees</th>
            <th className="px-3 py-2 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => (
            <UnifiedTaskRowContextMenu
              key={task.id}
              task={task}
            >
              <tr className="border-b border-gray-100 last:border-0 hover:bg-card-50/80">
                <td className="px-3 py-2">
                  <Link
                    href={`/tasks/${task.id}`}
                    className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {task.title}
                  </Link>
                </td>
                <td className="px-3 py-2 capitalize text-gray-700 dark:text-gray-300">
                  {formatStatusLabel(task.status)}
                </td>
                <td className="px-3 py-2 capitalize text-gray-700 dark:text-gray-300">
                  {task.priority || "—"}
                </td>
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{formatTaskDate(task.due_date)}</td>
                <td className="px-3 py-2">
                  <MultiUserDisplay
                    // Runtime contents may be strings or `{id}` objects; the
                    // consumer (`MultiUserDisplay`) handles both, so we forward
                    // the raw value when it's an array and otherwise fall back
                    // to an empty list.
                    assignedUsers={
                      Array.isArray(task.assigned_users)
                        ? (task.assigned_users as string[])
                        : []
                    }
                    assignedUser={readAssignedUserRef(task.assigned_user)}
                    compact
                    showTooltip={false}
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  <UnifiedTaskRowActions
                    task={task}
                    compact
                    refetch={refetch}
                  />
                </td>
              </tr>
            </UnifiedTaskRowContextMenu>
          ))}
        </tbody>
      </table>
    </div>
  );
}
